import axios from 'axios';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { AnyAction, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { RootState } from '..';
import { restHost } from '../../apiConfig';
import {
  deleteToken,
  formatUrl,
  getApiErrorMessage,
  getCookie,
} from '../../shared/utils';
import { SurveyType } from '../../shared/constants';
import restClient from '../../services/rest';
import { TabSchema } from '../GlobalTypes';
import {
  Careteam,
  CareteamInvite,
  CustomerStore,
  CustomerStoreActionTypes,
  Owner,
  CreateCustomerPayload,
  NewTechnicalSupportUserPayload,
  CustomerActionStatus,
  CustomerIssue,
  CustomerSurveyResponse,
  CachedCustomerSurveyResponses,
  CustomerSurveyHistory,
} from './customerTypes';

export const apiError = (error: any) => ({
  type: CustomerStoreActionTypes.CUSTOMERS_API_ERR,
  payload: { error: error },
});

export const setLoadState = (loadState: boolean) => ({
  type: CustomerStoreActionTypes.CUSTOMERS_SET_LOAD_STATE,
  payload: { isLoading: loadState },
});

export const setCreateUserSuccessful = (success: boolean) => ({
  type: CustomerStoreActionTypes.SUCCESS_CREATE_USER,
  payload: { createUserSuccessful: success },
});

export const populateCustomers = (customerList: Owner[]) => ({
  type: CustomerStoreActionTypes.POPULATE_CUSTOMERS,
  payload: { customerList },
});

export const populateCustomer = (data: Owner) => ({
  type: CustomerStoreActionTypes.POPULATE_CUSTOMER,
  payload: { customer: data },
});

export const setLastRefresh = () => ({
  type: CustomerStoreActionTypes.SET_LAST_REFRESH,
});

export const setIsRefreshing = (isRefreshing: boolean) => ({
  type: CustomerStoreActionTypes.SET_IS_REFRESHING,
  payload: isRefreshing,
});

export const populateCustomerCareTeam = (data: Owner) => ({
  type: CustomerStoreActionTypes.POPULATE_CUSTOMER_CARE_TEAM,
  payload: { careTeam: data },
});

export const clearCustomerAndCareTeam = () => ({
  type: CustomerStoreActionTypes.CLEAR_CUSTOMER_AND_CARE_TEAM,
});

export const populateCareTeamDetails = (data: {
  [key: string]: Careteam[];
}) => ({
  type: CustomerStoreActionTypes.POPULATE_CARE_TEAM_DETAILS,
  payload: { careTeamDetails: data },
});

export const populateCareteamInvites = (data: CareteamInvite[] | null) => ({
  type: CustomerStoreActionTypes.POPULATE_CARETEAM_INVITES,
  payload: { careteamInvites: data },
});

export const clearCareTeamDetails = () => ({
  type: CustomerStoreActionTypes.CLEAR_CARE_TEAM_DETAILS,
});

const creatingNewPrimaryContactDidSuccess = () => ({
  type: CustomerStoreActionTypes.CREATE_PRIMARY_CONTACT_SUCCESS,
});

export const setDeleteUserSuccessful = (success: boolean) => ({
  type: CustomerStoreActionTypes.SUCCESS_DELETE_USER,
  payload: { deleteUserSuccessful: success },
});

export const setActionStatus = (status: Partial<CustomerActionStatus>) => ({
  type: CustomerStoreActionTypes.SET_ACTION_STATUS,
  payload: status,
});

export const clearCustomerActionStatus = () => ({
  type: CustomerStoreActionTypes.CLEAR_ACTION_STATUS,
});

export const setProfilePictureLoading = (loading: boolean) => ({
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE_LOADING,
  payload: loading,
});
export const setProfilePicture = (profilePicture: string | null) => ({
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE,
  payload: profilePicture,
});
export const setProfilePictureError = (error: string | null) => ({
  type: CustomerStoreActionTypes.SET_PROFILE_PICTURE_ERROR,
  payload: error,
});

export const setMedicationImageLoading = (loading: boolean) => ({
  type: CustomerStoreActionTypes.SET_MEDICATION_IMAGE_LOADING,
  payload: loading,
});

export const setMedicationImage = (imageUrl: string | null) => ({
  type: CustomerStoreActionTypes.SET_MEDICATION_IMAGE,
  payload: imageUrl,
});

export const clearMedicationImage = () => ({
  type: CustomerStoreActionTypes.CLEAR_MEDICATION_IMAGE,
});

export const setCustomerIssuesLoading = (loading: boolean) => ({
  type: CustomerStoreActionTypes.SET_CUSTOMER_ISSUES_LOADING,
  payload: loading,
});

export const fetchCustomerIssuesSuccess = (issues: CustomerIssue[]) => ({
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_ISSUES_SUCCESS,
  payload: issues,
});

export const setCustomerSurveyLoading = (loading: boolean) => ({
  type: CustomerStoreActionTypes.SET_CUSTOMER_SURVEY_LOADING,
  payload: loading,
});

export const fetchCustomerSurveySuccess = (
  responses: CachedCustomerSurveyResponses,
) => ({
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_SUCCESS,
  payload: responses,
});

export const setCustomerWellnessLoading = (loading: boolean) => ({
  type: CustomerStoreActionTypes.SET_CUSTOMER_WELLNESS_LOADING,
  payload: loading,
});

export const fetchCustomerWellnessSuccess = (
  responses: CachedCustomerSurveyResponses,
) => ({
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_WELLNESS_SUCCESS,
  payload: responses,
});

export const customerSurveyHistoryRequest = (
  customerId: number,
  requestId: number,
) => ({
  type: CustomerStoreActionTypes.CUSTOMER_SURVEY_HISTORY_REQUEST,
  payload: { customerId, requestId },
});

export const fetchCustomerSurveyHistorySuccess = (
  history: CustomerSurveyHistory,
  requestId: number,
) => ({
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_SUCCESS,
  payload: { ...history, requestId },
});

export const fetchCustomerSurveyHistoryFailure = (
  requestId: number,
  error: string,
) => ({
  type: CustomerStoreActionTypes.FETCH_CUSTOMER_SURVEY_HISTORY_FAILURE,
  payload: { requestId, error },
});

export const addNewPrimaryContact = (contactDetails: Owner) => {
  return async (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    const { customer_id, ...params } = contactDetails;
    axios
      .post(formatUrl(restHost, `owners/${customer_id}/careteam`), params, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        if (!res.status) throw res;
        toast.success('The account has been created');
        dispatch(creatingNewPrimaryContactDidSuccess());
        dispatch(getCustomerCareTeam(customer_id));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        let message;
        if (err?.response?.status === 400) {
          message = 'This email address already exists, please try again';
        } else
          message = err?.response?.data || 'Failed to create a user account';
        dispatch(setLoadState(false));
        toast.error(message);
      });
  };
};

export const getCustomerList = (
  facilityId: number | null = null,
  privilegeId?: number,
) => {
  return (dispatch: ThunkDispatch<RootState, void, AnyAction> & Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `customers`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        params: {
          ...(facilityId ? { facility_id: facilityId } : {}),
          ...(privilegeId ? { privilege_id: privilegeId } : {}),
        },
      })
      .then((res) => {
        const customers =
          res.data.length > 0
            ? res.data.map((obj: { created_at: Date }) => {
                const createdAtDateTime = DateTime.fromJSDate(
                  new Date(obj.created_at),
                );
                const relativeCreatedAt = createdAtDateTime.toRelative(
                  DateTime.now(),
                );
                return {
                  ...obj,
                  created_at: relativeCreatedAt,
                  created_at_string: new Date(obj.created_at).toLocaleString(),
                };
              })
            : [];
        dispatch(populateCustomers(customers));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getFacilityCustomers = (facilityId: number) => {
  return getCustomerList(facilityId);
};

export const createUser = (data: Partial<CreateCustomerPayload>) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .post(formatUrl(restHost, 'facility/customer'), data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        if (!res.status) throw res;
        toast.success('The account has been created');
        dispatch(setCreateUserSuccessful(true));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        let message;
        if (err?.response?.status === 409) {
          // Patient creates run the duplicate identity check and can return
          // { action } instead of the email-dup string. Keep the message
          // vague — it must not reveal that a matching patient record
          // exists (inference/enumeration risk).
          if (err?.response?.data?.action) {
            message = 'Something went wrong. Please contact customer care.';
          } else {
            message = 'This username already exists, please try again';
          }
        } else message = 'Failed to create a user account';
        toast.error(message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getCustomer = (customerId: number) => {
  return (dispatch: ThunkDispatch<RootState, void, AnyAction> & Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `customer/${customerId}`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        if (res.data.is_patient) {
          dispatch(getCustomerCareTeam(res.data.customer_id));
        }
        dispatch(populateCustomer(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getCustomerCareTeam = (customerId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .get(formatUrl(restHost, `customer/${customerId}/careteam`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateCustomerCareTeam(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data?.message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getCareTeamDetails = (customerId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    axios
      .get(formatUrl(restHost, `customer/${customerId}`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateCareTeamDetails(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data || 'Failed to load user details');
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const getPendingCareteamInvites = (customerId: number) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    axios
      .get(formatUrl(restHost, `customer/${customerId}/careteam/invites`), {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(populateCareteamInvites(res.data));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data || 'Failed to load pending invites');
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const createCareteamInvite = (
  customerId: number,
  userProps: Partial<CareteamInvite>,
  type = 'send',
) => {
  return (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    axios
      .post(
        formatUrl(restHost, `customer/${customerId}/careteam/invites`),
        userProps,
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then(() => {
        dispatch(getPendingCareteamInvites(customerId));
        toast.success('Invite sent successfully');
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data || `Failed to ${type} invite`);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const deleteCareteamInvite = (customerId: number, email: string) => {
  return (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    axios
      .delete(
        formatUrl(restHost, `customer/${customerId}/careteam/invites/${email}`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      )
      .then(() => {
        toast.success('Invite deleted successfully');
        dispatch(getPendingCareteamInvites(customerId));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data || 'Failed to delete invite');
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const sideBarTabSelection = (selectedTab: TabSchema) => ({
  type: CustomerStoreActionTypes.CUSTOMERS_SIDE_BAR_TAB_SELECTION,
  payload: { selectedTab },
});

export const saveOwnerPin = (ownerPin: string | null) => ({
  type: CustomerStoreActionTypes.SAVE_OWNER_PIN,
  payload: { ownerPin: ownerPin },
});

export const fetchOwnerPin = (password: string, ownerId: number) => {
  const params = {
    password: password,
  };

  return (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    axios
      .post(formatUrl(restHost, `customer/${ownerId}/pin`), params, {
        headers: {
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        dispatch(saveOwnerPin(res.data.pin));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        toast.error(err?.response?.data);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

// API to create new engineer and customer care
export const createTechnicalSupportUsers = (
  data: Partial<NewTechnicalSupportUserPayload>,
  privilege = 'customer care',
) => {
  return (dispatch: Dispatch) => {
    dispatch(setLoadState(true));
    return axios
      .post(formatUrl(restHost, 'eng/customer'), data, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
      })
      .then((res) => {
        if (!res.status) throw res;
        toast.success('The account has been created');
        dispatch(setCreateUserSuccessful(true));
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        let message;
        if (err?.response?.status === 409) {
          message = 'This user already exists, please try again';
        } else message = `Failed to create ${privilege} account`;
        toast.error(message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
      });
  };
};

export const deleteUser = (
  customerId: number,
  requestBody?: {
    deletion_reason?: string;
    additional_details?: string;
  },
  showToast: boolean = true,
) => {
  return (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));
    axios
      .delete(formatUrl(restHost, `customers/${customerId}`), {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        data: requestBody, // Send request body with DELETE (optional)
      })
      .then((res) => {
        if (res.status === 200) {
          if (showToast) toast.success('User deleted successfully');
          dispatch(setDeleteUserSuccessful(true));
          dispatch(setActionStatus({ success: true, error: null }));
        } else {
          throw res;
        }
        dispatch(setLoadState(false));
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          deleteToken();
        }
        const message = 'Failed to delete user';
        if (showToast) toast.error(message);
        dispatch(apiError(err));
        dispatch(setLoadState(false));
        dispatch(setActionStatus({ success: false, error: message }));
      });
  };
};

export const uploadProfilePicture = (customerId: number, imageBlob: Blob) => {
  return async (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setLoadState(true));

    try {
      const response = await axios({
        method: 'POST',
        url: formatUrl(restHost, `customers/${customerId}/profile_picture`),
        headers: {
          'Content-Type': 'image/png',
          Authorization: `Bearer ${getCookie('access_token')}`,
        },
        data: imageBlob,
      });

      if (response.status === 200) {
        dispatch(setActionStatus({ success: true, error: null }));
        toast.success('Profile picture updated successfully');
      } else {
        // error handling for non-200 status (403 etc)
        throw response;
      }
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message || 'Failed to update profile picture';
      dispatch(setActionStatus({ success: false, error: errorMessage }));
      toast.error(errorMessage);
    } finally {
      dispatch(setLoadState(false));
    }
  };
};

export const fetchProfilePicture = (customerId: number) => {
  return async (
    dispatch: ThunkDispatch<CustomerStore, void, AnyAction> & Dispatch,
  ) => {
    dispatch(setProfilePictureLoading(true));
    dispatch(setProfilePictureError(null));
    try {
      const response = await axios.get(
        formatUrl(restHost, `customers/${customerId}/profile_picture`),
        {
          headers: {
            Accept: 'image/png',
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
          responseType: 'blob',
        },
      );
      // Convert blob to data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch(setProfilePicture(reader.result as string));
        dispatch(setProfilePictureLoading(false));
      };
      reader.onerror = () => {
        dispatch(setProfilePicture(null));
        dispatch(setProfilePictureLoading(false));
        dispatch(setProfilePictureError('Failed to load profile picture'));
      };
      reader.readAsDataURL(response.data);
    } catch (err: any) {
      dispatch(setProfilePicture(null));
      dispatch(setProfilePictureLoading(false));
      dispatch(setProfilePictureError('Failed to load profile picture'));
    }
  };
};

export const fetchMedicationImage = (drugId: number) => {
  return async (dispatch: any) => {
    dispatch(setMedicationImageLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `customer/medication/${drugId}/image`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
          responseType: 'blob',
        },
      );
      // Convert blob to data URL
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch(setMedicationImage(reader.result as string));
        dispatch(setMedicationImageLoading(false));
      };
      reader.onerror = () => {
        dispatch(setMedicationImage(null));
        dispatch(setMedicationImageLoading(false));
      };
      reader.readAsDataURL(response.data);
    } catch (err: any) {
      dispatch(setMedicationImage(null));
      dispatch(setMedicationImageLoading(false));
    }
  };
};

export const fetchCustomerIssues = (customerId: number) => {
  return async (dispatch: any) => {
    dispatch(setCustomerIssuesLoading(true));
    try {
      const response = await axios.get(
        formatUrl(restHost, `customers/${customerId}/issues`),
        {
          headers: {
            Authorization: `Bearer ${getCookie('access_token')}`,
          },
        },
      );
      dispatch(fetchCustomerIssuesSuccess({ ...response.data, customerId }));
    } catch (err: any) {
      toast.error(
        err?.response?.data || err.message || 'Failed to fetch customer issues',
      );
    } finally {
      dispatch(setCustomerIssuesLoading(false));
    }
  };
};

// GET /customers/:id/satisfaction_survey stores both CSAT (1-4) and daily
// wellness check (1-5) rows; `surveyTypeId` selects which scale comes back.
// Uses the generated client so failures reject (and toast) instead of being
// swallowed by the legacy axios interceptor.
const getCustomerSurveyRows = async (
  customerId: number,
  surveyTypeId: SurveyType,
): Promise<CustomerSurveyResponse[]> => {
  const rows = await restClient.customers.getCustomerSurvey(
    customerId,
    surveyTypeId,
  );
  return (rows ?? []) as CustomerSurveyResponse[];
};

export const fetchCustomerSurveyResponses = (customerId: number) => {
  return async (dispatch: any) => {
    dispatch(setCustomerSurveyLoading(true));
    try {
      const rows = await getCustomerSurveyRows(
        customerId,
        SurveyType.HubOverallExperience,
      );
      dispatch(fetchCustomerSurveySuccess({ ...rows, customerId }));
    } catch (err: any) {
      toast.error(getApiErrorMessage(err, 'Failed to fetch survey responses'));
      dispatch(fetchCustomerSurveySuccess([]));
    } finally {
      dispatch(setCustomerSurveyLoading(false));
    }
  };
};

// Daily wellness survey (DWS) history for the DWS details panel.
export const fetchCustomerWellnessResponses = (customerId: number) => {
  return async (dispatch: any) => {
    dispatch(setCustomerWellnessLoading(true));
    try {
      const rows = await getCustomerSurveyRows(
        customerId,
        SurveyType.HubDailyWellnessCheck,
      );
      dispatch(fetchCustomerWellnessSuccess({ ...rows, customerId }));
    } catch (err: any) {
      toast.error(
        getApiErrorMessage(err, 'Failed to fetch wellness survey responses'),
      );
      dispatch(fetchCustomerWellnessSuccess([]));
    } finally {
      dispatch(setCustomerWellnessLoading(false));
    }
  };
};

// GET /customers/:id/satisfaction_survey?days=&time_zone= — every survey type
// over the same rolling window (ending today in the viewer's zone) that the
// owner survey insights use, so the list and the tiles cover the same days.
// Each request carries a sequence id so a slow response for a previous
// patient or window cannot overwrite the rows of the one now shown.
let surveyHistoryRequestSeq = 0;

export const fetchCustomerSurveyHistory = (
  customerId: number,
  days: number,
  timeZone: string,
) => {
  return async (dispatch: any) => {
    const requestId = ++surveyHistoryRequestSeq;
    dispatch(customerSurveyHistoryRequest(customerId, requestId));
    try {
      const rows = await restClient.customers.getCustomerSurvey(
        customerId,
        undefined,
        days,
        timeZone,
      );
      dispatch(
        fetchCustomerSurveyHistorySuccess(
          {
            customerId,
            requestedDays: days,
            rows: (rows ?? []) as CustomerSurveyResponse[],
          },
          requestId,
        ),
      );
    } catch (err: any) {
      const message = getApiErrorMessage(
        err,
        'Failed to fetch survey insights',
      );
      toast.error(message);
      dispatch(fetchCustomerSurveyHistoryFailure(requestId, message));
    }
  };
};
